import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { syncFromAirtable, getAirtableRecords, getAirtableSyncStatus, updateAirtableRecord, clearLocalEdits } from '../services/api';
import { RefreshCw, Database, Clock, Users, Save, X, Edit2, RotateCcw, ChevronLeft, ChevronRight, Search, ExternalLink } from 'lucide-react';

// Editable fields that can be synced back to Airtable
const EDITABLE_FIELDS = new Set([
  'Company Name (clean)',
  'First Name',
  'Last Name',
  'Email 🚨',
  '50 Character Solution Statement',
  'Stage',
  'Status',
  'Selection Status',
  'Pitch Status',
  'Application Date',
  'BT Feedback',
  'Solution Pitch (All Luma Questions)',
]);

// Priority fields to show first (in order)
const PRIORITY_FIELDS = [
  'Company Name (clean)',
  'First Name',
  'Last Name',
  'Email 🚨',
  '50 Character Solution Statement',
  'Broad Category',
  'Stage',
  'Status',
  'Selection Status',
  'Pitch Status',
  'Event Name From Luma',
  'Application Date',
  'Referral FN',
  'Website',
  'Linkedin',
  'BT Feedback',
];

const ResizableHeader = ({ columnKey, width, onResize, children }) => {
  const isResizingRef = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(0);

  const handleMouseMove = useCallback((e) => {
    if (!isResizingRef.current) return;
    const diff = e.clientX - startX.current;
    const newWidth = Math.max(50, startWidth.current + diff);
    onResize(columnKey, newWidth);
  }, [columnKey, onResize]);

  const handleMouseUp = useCallback(() => {
    isResizingRef.current = false;
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, [handleMouseMove]);

  const handleMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    isResizingRef.current = true;
    startX.current = e.clientX;
    startWidth.current = width;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  return (
    <th
      className="text-left px-2 py-2 text-xs font-medium text-slate-400 whitespace-nowrap relative select-none border-r border-slate-700"
      style={{ width, minWidth: 50 }}
    >
      {children}
      <div
        className="absolute right-0 top-0 bottom-0 w-3 cursor-col-resize hover:bg-blue-500/30 group flex items-center justify-center"
        onMouseDown={handleMouseDown}
      >
        <div className="w-0.5 h-4 bg-slate-500 group-hover:bg-blue-400 rounded" />
      </div>
    </th>
  );
};

const AdminAirtableSync = () => {
  const [records, setRecords] = useState([]);
  const [allColumns, setAllColumns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncResult, setSyncResult] = useState(null);
  const [pagination, setPagination] = useState({ skip: 0, limit: 100, total: 0 });
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [columnWidths, setColumnWidths] = useState({});

  // Extract all unique column names from records and sort them
  const extractColumns = useCallback((records) => {
    const columnSet = new Set();
    records.forEach(record => {
      if (record.fields) {
        Object.keys(record.fields).forEach(key => columnSet.add(key));
      }
    });
    
    // Sort: priority fields first, then alphabetically
    const allCols = Array.from(columnSet);
    const prioritySet = new Set(PRIORITY_FIELDS);
    
    const sortedColumns = [
      ...PRIORITY_FIELDS.filter(f => columnSet.has(f)),
      ...allCols.filter(f => !prioritySet.has(f)).sort()
    ];
    
    return sortedColumns;
  }, []);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [recordsData, statusData] = await Promise.all([
        getAirtableRecords(pagination.skip, pagination.limit),
        getAirtableSyncStatus()
      ]);
      setRecords(recordsData.records);
      setPagination(prev => ({ ...prev, total: recordsData.total }));
      setSyncStatus(statusData);
      
      // Extract columns from data
      const cols = extractColumns(recordsData.records);
      setAllColumns(cols);
      
      // Initialize column widths for new columns
      setColumnWidths(prev => {
        const newWidths = { ...prev };
        cols.forEach(col => {
          if (!newWidths[col]) {
            // Set default width based on column name length
            newWidths[col] = Math.max(100, Math.min(250, col.length * 8 + 40));
          }
        });
        return newWidths;
      });
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  }, [pagination.skip, pagination.limit, extractColumns]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleColumnResize = useCallback((key, newWidth) => {
    setColumnWidths(prev => ({
      ...prev,
      [key]: newWidth
    }));
  }, []);

  const handleSync = async () => {
    try {
      setSyncing(true);
      setSyncResult(null);
      const result = await syncFromAirtable();
      setSyncResult(result);
      await loadData();
    } catch (error) {
      console.error('Error syncing:', error);
      setSyncResult({ success: false, message: error.response?.data?.detail || 'Sync failed' });
    } finally {
      setSyncing(false);
    }
  };

  const handleEditStart = (recordId, field, currentValue) => {
    setEditingCell({ recordId, field });
    setEditValue(currentValue || '');
  };

  const handleEditCancel = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const handleEditSave = async () => {
    if (!editingCell) return;
    
    try {
      setSaving(true);
      await updateAirtableRecord(editingCell.recordId, {
        [editingCell.field]: editValue
      });
      
      setRecords(prev => prev.map(record => {
        if (record.id === editingCell.recordId) {
          return {
            ...record,
            fields: { ...record.fields, [editingCell.field]: editValue },
            has_local_edits: true,
            local_edited_fields: [...new Set([...(record.local_edited_fields || []), editingCell.field])]
          };
        }
        return record;
      }));
      
      setEditingCell(null);
      setEditValue('');
    } catch (error) {
      console.error('Error saving:', error);
      alert('Failed to save: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  const handleClearLocalEdits = async (recordId) => {
    if (!window.confirm('This will revert all local edits and restore Airtable data. Continue?')) return;
    
    try {
      await clearLocalEdits(recordId);
      await loadData();
    } catch (error) {
      console.error('Error clearing edits:', error);
      alert('Failed to clear edits');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleEditSave();
    } else if (e.key === 'Escape') {
      handleEditCancel();
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const isLinkField = (fieldName) => {
    const lower = fieldName.toLowerCase();
    return lower.includes('website') || lower.includes('linkedin') || lower.includes('url') || lower.includes('link');
  };

  const filteredRecords = records.filter(record => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    const fields = record.fields || {};
    return Object.values(fields).some(val => 
      String(val || '').toLowerCase().includes(term)
    );
  });

  const totalPages = Math.ceil(pagination.total / pagination.limit);
  const currentPage = Math.floor(pagination.skip / pagination.limit) + 1;

  const renderCellValue = (record, columnKey) => {
    const value = record.fields?.[columnKey] || '';
    const isEdited = record.local_edited_fields?.includes(columnKey);
    const isCurrentlyEditing = editingCell?.recordId === record.id && editingCell?.field === columnKey;
    const isEditable = EDITABLE_FIELDS.has(columnKey);
    const isLink = isLinkField(columnKey);

    if (isCurrentlyEditing) {
      return (
        <div className="flex items-center gap-1">
          <Input
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className="h-7 text-xs bg-slate-700 border-blue-500 text-white"
            autoFocus
          />
          <Button
            size="sm"
            variant="ghost"
            onClick={handleEditSave}
            disabled={saving}
            className="h-7 w-7 p-0 text-green-400 hover:text-green-300"
          >
            <Save className="w-3 h-3" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={handleEditCancel}
            className="h-7 w-7 p-0 text-red-400 hover:text-red-300"
          >
            <X className="w-3 h-3" />
          </Button>
        </div>
      );
    }

    if (isLink && value) {
      const url = String(value).startsWith('http') ? value : `https://${value}`;
      return (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-400 hover:text-blue-300 flex items-center gap-1 truncate"
        >
          <span className="truncate">{String(value).replace(/^https?:\/\//, '').substring(0, 30)}</span>
          <ExternalLink className="w-3 h-3 flex-shrink-0" />
        </a>
      );
    }

    const displayValue = typeof value === 'object' ? JSON.stringify(value) : String(value || '-');

    return (
      <div
        className={`truncate ${isEditable ? 'cursor-pointer' : 'cursor-default'} group flex items-center gap-1 ${isEdited ? 'text-yellow-300' : ''}`}
        onClick={() => isEditable && handleEditStart(record.id, columnKey, value)}
        title={displayValue}
      >
        <span className="truncate">{displayValue}</span>
        {isEditable && (
          <Edit2 className="w-3 h-3 opacity-0 group-hover:opacity-50 flex-shrink-0" />
        )}
        {isEdited && (
          <span className="text-[10px] bg-yellow-600/30 px-1 rounded flex-shrink-0">edited</span>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white" data-testid="admin-airtable-sync">
      {/* Header */}
      <div className="border-b border-slate-800 bg-slate-900/50 sticky top-0 z-20">
        <div className="px-4 py-3">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.location.href = '/'}
                className="border-slate-700 text-slate-300 hover:bg-slate-800"
              >
                ← Back to Site
              </Button>
              <h1 className="text-xl font-bold text-white">Admin Dashboard</h1>
            </div>
            
            <div className="flex items-center gap-4">
              <span className="text-sm text-slate-400">
                {localStorage.getItem('adminEmail')}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  localStorage.removeItem('adminToken');
                  localStorage.removeItem('adminEmail');
                  window.location.href = '/admin';
                }}
                className="border-slate-700 text-slate-300 hover:bg-slate-800"
              >
                Logout
              </Button>
            </div>
          </div>
          
          {/* Navigation Tabs */}
          <div className="flex gap-1 mt-3 overflow-x-auto">
            {[
              { id: 'applications', label: 'Applications', path: '/admin/applications' },
              { id: 'airtable-sync', label: 'Airtable Sync', path: '/admin/airtable-sync' },
              { id: 'testimonials', label: 'Testimonials', path: '/admin/testimonials' },
              { id: 'startups', label: 'Startups', path: '/admin/startups' },
              { id: 'sponsors', label: 'Sponsors', path: '/admin/sponsors' },
              { id: 'podcast', label: 'Podcast', path: '/admin/podcast' },
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => window.location.href = item.path}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                  item.id === 'airtable-sync'
                    ? 'bg-blue-600 text-white'
                    : 'text-slate-400 hover:text-white hover:bg-slate-800'
                }`}
              >
                {item.id === 'airtable-sync' && <RefreshCw className="w-4 h-4" />}
                {item.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-4 space-y-4">
        {/* Header Row */}
        <div className="flex justify-between items-start gap-4 flex-wrap">
          <div>
            <h2 className="text-2xl font-bold text-white">Airtable Sync</h2>
            <p className="text-slate-400 text-sm mt-1">
              All {allColumns.length} columns from Airtable • Click editable cells to modify • Drag column edges to resize
            </p>
          </div>
          <Button
            onClick={handleSync}
            disabled={syncing}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="sync-button"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing...' : 'Sync from Airtable'}
          </Button>
        </div>

        {/* Status Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700">
            <div className="flex items-center gap-2">
              <Database className="w-4 h-4 text-blue-400" />
              <div>
                <p className="text-slate-400 text-xs">Total Records</p>
                <p className="text-xl font-bold text-white">{syncStatus?.total_records || 0}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-green-400" />
              <div>
                <p className="text-slate-400 text-xs">Last Sync</p>
                <p className="text-sm font-medium text-white">
                  {syncStatus?.last_sync ? formatDate(syncStatus.last_sync) : 'Never'}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-purple-400" />
              <div>
                <p className="text-slate-400 text-xs">Columns</p>
                <p className="text-xl font-medium text-white">{allColumns.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700">
            <div className="flex items-center gap-2">
              <Search className="w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search all fields..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="h-8 text-sm bg-slate-700 border-slate-600 text-white"
              />
            </div>
          </div>
        </div>

        {/* Sync Result Alert */}
        {syncResult && (
          <div
            className={`p-3 rounded-lg border text-sm ${
              syncResult.success
                ? 'bg-green-900/30 border-green-700 text-green-300'
                : 'bg-red-900/30 border-red-700 text-red-300'
            }`}
          >
            <p className="font-medium">
              {syncResult.success ? 'Sync Completed!' : 'Sync Failed'}
            </p>
            {syncResult.success && (
              <p className="text-xs mt-1">
                New: {syncResult.synced} | Updated: {syncResult.updated} | 
                Preserved Local Edits: {syncResult.preserved_local_edits} | 
                Total: {syncResult.total_fetched}
              </p>
            )}
          </div>
        )}

        {/* Records Table */}
        <div className="bg-slate-800/50 rounded-lg border border-slate-700 overflow-hidden">
          <div className="overflow-x-auto" style={{ maxHeight: 'calc(100vh - 340px)' }}>
            <table className="w-full text-sm border-collapse">
              <thead className="bg-slate-900/80 sticky top-0 z-10">
                <tr>
                  <th 
                    className="text-left px-2 py-2 text-xs font-medium text-slate-400 sticky left-0 bg-slate-900 z-20 border-r border-slate-700" 
                    style={{ width: 40, minWidth: 40 }}
                  >
                    #
                  </th>
                  {allColumns.map((col) => (
                    <ResizableHeader
                      key={col}
                      columnKey={col}
                      width={columnWidths[col] || 150}
                      onResize={handleColumnResize}
                    >
                      <span className="flex items-center gap-1">
                        <span className="truncate">{col}</span>
                        {EDITABLE_FIELDS.has(col) && <Edit2 className="w-3 h-3 opacity-50 flex-shrink-0" />}
                      </span>
                    </ResizableHeader>
                  ))}
                  <th className="text-left px-2 py-2 text-xs font-medium text-slate-400 sticky right-0 bg-slate-900 z-20 border-l border-slate-700" style={{ width: 60 }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/50">
                {loading ? (
                  <tr>
                    <td colSpan={allColumns.length + 2} className="px-4 py-8 text-center text-slate-400">
                      Loading...
                    </td>
                  </tr>
                ) : filteredRecords.length === 0 ? (
                  <tr>
                    <td colSpan={allColumns.length + 2} className="px-4 py-8 text-center text-slate-400">
                      {searchTerm ? 'No matching records' : 'No records synced yet. Click "Sync from Airtable" to import.'}
                    </td>
                  </tr>
                ) : (
                  filteredRecords.map((record, idx) => (
                    <tr
                      key={record.id}
                      className={`hover:bg-slate-700/30 ${record.has_local_edits ? 'bg-yellow-900/10' : ''}`}
                    >
                      <td className="px-2 py-1.5 text-xs text-slate-500 sticky left-0 bg-slate-800/95 border-r border-slate-700 z-10">
                        {pagination.skip + idx + 1}
                      </td>
                      {allColumns.map((col) => (
                        <td
                          key={col}
                          className="px-2 py-1.5 text-xs text-slate-300 border-r border-slate-700/30"
                          style={{ width: columnWidths[col] || 150, maxWidth: columnWidths[col] || 150 }}
                        >
                          {renderCellValue(record, col)}
                        </td>
                      ))}
                      <td className="px-2 py-1.5 sticky right-0 bg-slate-800/95 border-l border-slate-700 z-10">
                        {record.has_local_edits && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleClearLocalEdits(record.id)}
                            className="h-6 w-6 p-0 text-yellow-400 hover:text-yellow-300"
                            title="Revert to Airtable data"
                          >
                            <RotateCcw className="w-3 h-3" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-700 bg-slate-900/50">
            <p className="text-xs text-slate-400">
              Showing {pagination.skip + 1} - {Math.min(pagination.skip + pagination.limit, pagination.total)} of {pagination.total} records
              {searchTerm && ` (${filteredRecords.length} matching)`}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPagination(prev => ({ ...prev, skip: prev.skip - prev.limit }))}
                disabled={pagination.skip === 0}
                className="h-7 border-slate-600 text-slate-300 hover:bg-slate-700"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-xs text-slate-400">
                Page {currentPage} / {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPagination(prev => ({ ...prev, skip: prev.skip + prev.limit }))}
                disabled={pagination.skip + pagination.limit >= pagination.total}
                className="h-7 border-slate-600 text-slate-300 hover:bg-slate-700"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Legend */}
        <div className="flex items-center gap-4 text-xs text-slate-500 flex-wrap">
          <span className="flex items-center gap-1">
            <Edit2 className="w-3 h-3" /> = Editable field
          </span>
          <span className="flex items-center gap-1">
            <span className="bg-yellow-600/30 px-1 rounded">edited</span> = Local edit preserved
          </span>
          <span className="flex items-center gap-1">
            <RotateCcw className="w-3 h-3" /> = Revert to Airtable
          </span>
        </div>
      </div>
    </div>
  );
};

export default AdminAirtableSync;
